import { canManageTeam } from "@69ly/shared";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useSearchParams } from "react-router";
import { Icon } from "../components/Icon.tsx";
import { Banner, Loading } from "../components/Status.tsx";
import { useDiscordCode, useLinkDiscord, useLinkDiscordTeam } from "../lib/discord-queries.ts";
import { errorText } from "../lib/errors.ts";
import { useTeams } from "../lib/queries.ts";

/** Opened from the bot's /69ly link: links this Discord account to mine, and asks about recording. */
export function DiscordLinkPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const code = params.get("code") ?? "";
  const info = useDiscordCode(code);
  const link = useLinkDiscord();
  // Unticked: agreeing to be recorded is a choice the player makes (GDPR: no pre-ticked consent).
  const [consent, setConsent] = useState(false);

  if (info.isPending && code) return <Loading />;
  return (
    <div className="page page-narrow discord-page">
      <div className="card discord-card">
        <span className="discord-mark" aria-hidden="true">
          <Icon name="mic" size={22} />
        </span>
        <h1 className="section-title">{t("discord.linkTitle")}</h1>
        {!code || info.error || info.data?.kind !== "user" ? (
          <Banner tone="warning">{info.error ? errorText(t, info.error) : t("discord.noCode")}</Banner>
        ) : link.data ? (
          <>
            <Banner tone="success">{t(link.data.consent ? "discord.linkedConsent" : "discord.linkedNoConsent", { name: link.data.discordName })}</Banner>
            <Link to="/settings/account" className="btn btn-primary">
              {t("discord.toTeam")}
            </Link>
          </>
        ) : (
          <form
            className="discord-form"
            onSubmit={(e) => {
              e.preventDefault();
              link.mutate({ code, consent });
            }}
          >
            <p>{t("discord.linkIntro", { name: info.data.discordName })}</p>
            <label className="discord-consent">
              <input type="checkbox" checked={consent} onChange={(e) => setConsent(e.target.checked)} />
              <span>
                <b>{t("discord.consentLabel")}</b>
                <span className="muted small">{t("discord.consentHelp")}</span>
              </span>
            </label>
            {link.error && <p className="field-error">{errorText(t, link.error)}</p>}
            <button type="submit" className="btn btn-primary" disabled={link.isPending}>
              {t("discord.link")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}

/** Opened from the bot's /69ly team: an owner or captain picks the team this Discord server records for. */
export function DiscordTeamPage() {
  const { t } = useTranslation();
  const [params] = useSearchParams();
  const code = params.get("code") ?? "";
  const info = useDiscordCode(code);
  const teams = (useTeams().data ?? []).filter((x) => canManageTeam(x.role));
  const link = useLinkDiscordTeam();
  const [teamId, setTeamId] = useState("");
  const chosen = teamId || teams[0]?.id || "";

  if (info.isPending && code) return <Loading />;
  return (
    <div className="page page-narrow discord-page">
      <div className="card discord-card">
        <span className="discord-mark" aria-hidden="true">
          <Icon name="mic" size={22} />
        </span>
        <h1 className="section-title">{t("discord.teamTitle")}</h1>
        {!code || info.error || info.data?.kind !== "guild" ? (
          <Banner tone="warning">{info.error ? errorText(t, info.error) : t("discord.noCode")}</Banner>
        ) : link.isSuccess ? (
          <>
            <Banner tone="success">{t("discord.teamLinked", { server: info.data.guildName, team: teams.find((x) => x.id === chosen)?.name ?? "" })}</Banner>
            <Link to="/settings/team#discord" className="btn btn-primary">
              {t("discord.toTeam")}
            </Link>
          </>
        ) : teams.length === 0 ? (
          <Banner tone="warning">{t("discord.teamNoTeam")}</Banner>
        ) : (
          <form
            className="discord-form"
            onSubmit={(e) => {
              e.preventDefault();
              link.mutate({ code, teamId: chosen });
            }}
          >
            <p>{t("discord.teamIntro", { server: info.data.guildName })}</p>
            <label className="field">
              <span>{t("discord.team")}</span>
              <select value={chosen} onChange={(e) => setTeamId(e.target.value)}>
                {teams.map((x) => (
                  <option key={x.id} value={x.id}>
                    {x.name}
                  </option>
                ))}
              </select>
            </label>
            {link.error && <p className="field-error">{errorText(t, link.error)}</p>}
            <button type="submit" className="btn btn-primary" disabled={link.isPending}>
              {t("discord.teamLink")}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
